import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { InfoDot } from './InfoDot';

export interface FormTextFieldProps<TFieldValues extends FieldValues> {
  /** Field path; the component reads `control` from the surrounding `FormProvider`. */
  name: FieldPath<TFieldValues>;
  label: string;
  /** Optional info tooltip beside the label. */
  info?: string;
  inputMode?: 'numeric' | 'text';
  disabled?: boolean;
  /**
   * Marks the field as holding sensitive data (e.g. card number, CVV, bank account). Disables
   * browser autofill/autocorrect and signals password managers (1Password, LastPass) to skip it,
   * so the value is never captured, suggested, or stored. Use for any PCI/PII input.
   */
  sensitive?: boolean;
  /** Transform raw input (formatting / digit-stripping) before it reaches form state. */
  format?: (value: string) => string;
  /**
   * Resolve a validation message to display text. Schemas commonly store i18n keys as the
   * message; pass an app-scoped translator (e.g. a namespaced `t`). Defaults to identity, so a
   * schema that stores already-display-ready messages needs no translator.
   */
  translateError?: (message: string) => string;
}

/**
 * Generic Controller-wrapped text input following the Field + FieldError pattern. App- and
 * i18n-namespace-agnostic: pair it with react-hook-form's `FormProvider` and (optionally) pass a
 * `translateError` to localize messages. The canonical text field for any form in either portal.
 */
export function FormTextField<TFieldValues extends FieldValues>({
  name,
  label,
  info,
  inputMode = 'text',
  disabled,
  sensitive,
  format,
  translateError,
}: FormTextFieldProps<TFieldValues>) {
  // No-capture attributes for PCI/PII fields: suppress browser autofill/autocorrect and the
  // 1Password / LastPass overlays so the raw value is never persisted outside form memory.
  const sensitiveProps = sensitive
    ? ({
        autoComplete: 'off',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        spellCheck: false,
        'data-1p-ignore': true,
        'data-lpignore': 'true',
      } as const)
    : undefined;
  return (
    <Controller<TFieldValues>
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {/* Fixed-height label row so fields with an info icon (a 24px button) align with
              plain-label fields when laid out side by side in a grid. */}
          <div className="flex min-h-6 items-center gap-1">
            <FieldLabel htmlFor={name}>{label}</FieldLabel>
            {info && <InfoDot text={info} />}
          </div>
          <Input
            id={name}
            inputMode={inputMode}
            disabled={disabled}
            {...sensitiveProps}
            aria-invalid={fieldState.invalid}
            value={typeof field.value === 'string' ? field.value : ''}
            onBlur={field.onBlur}
            onChange={(event) => field.onChange(format ? format(event.target.value) : event.target.value)}
          />
          {fieldState.error?.message && (
            <FieldError>
              {translateError ? translateError(fieldState.error.message) : fieldState.error.message}
            </FieldError>
          )}
        </Field>
      )}
    />
  );
}
